/**
 * Aether onboarding tour — a lightweight spotlight product tour for customers
 * and partners (admins know their own tool, so they don't get one).
 *
 * Matches the "tut-*" guided-tour style used across the AE plugins: a dimmed
 * overlay with a spotlight hole cut around the target element + a glass card.
 *
 *  - auto-starts ONCE per role on first dashboard visit (localStorage flag)
 *  - steps whose target element isn't in the DOM are skipped gracefully, so a
 *    role-hidden anchor never breaks the run
 *  - restartable from anywhere via `startAetherTour()` (window event)
 *  - Esc / arrow keys supported
 *
 * Targets are matched by `[data-tour="…"]` anchors placed on a few stable
 * elements (sidebar, tabs, quick tiles, affiliate card).
 */
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';

const TOUR_VERSION = 'v3'; // bumped: steps retargeted + real skipping

const STEPS = {
  customer: [
    { title: 'Willkommen 👋', body: 'Kurze Runde durch dein Dashboard — dauert nur ein paar Sekunden.' },
    { target: '[data-testid="aether-nav-customer-overview"]', placement: 'right', title: 'Übersicht', body: 'Dein Start: Neuigkeiten, Kennzahlen und Schnellzugriff auf einen Blick.' },
    { target: '[data-testid="aether-nav-customer-products"]', placement: 'right', title: 'Produkte', body: 'Alle deine Extensions mit Download und aktueller Version.' },
    { target: '[data-testid="aether-nav-customer-orders"]', placement: 'right', title: 'Bestellungen', body: 'Jeder Kauf mit Lizenzschlüssel, Download und Rechnung — alles in einer Zeile.' },
    { target: '[data-testid="aether-nav-customer-assets"]', placement: 'right', title: 'Assets', body: 'Zubehör zum Direkt-Laden, ohne Lizenz.' },
    { target: '[data-testid="aether-nav-customer-profile"]', placement: 'right', title: 'Profil', body: 'Name, Passwort und dein Konto.' },
    { target: '[data-testid="aether-nav-logout"]', placement: 'right', title: 'Abmelden', body: 'Und hier meldest du dich wieder ab. Das war’s — viel Spaß!' },
  ],
  // Partners navigate via the SIDEBAR (same as customers) — the old step
  // pointed at the inline tab row, which now only holds the cog, so the ring
  // framed an empty strip.
  partner: [
    { title: 'Willkommen im Partner-Bereich', body: 'Kurze Runde durch deine wichtigsten Funktionen?' },
    { target: '[data-tour="affiliate"]', placement: 'bottom', title: 'Dein Partner-Link', body: 'Teile diesen Link — jede Bestellung darüber bringt dir Provision.' },
    { target: '[data-testid="aether-nav-customer-performance"]', placement: 'right', title: 'Performance', body: 'Provisions-Verlauf der letzten 14 Tage und deine stärksten Produkte.' },
    { target: '[data-testid="aether-nav-customer-products"]', placement: 'right', title: 'Produkte', body: 'Deine eigenen Extensions mit Download und aktueller Version.' },
    { target: '[data-testid="aether-nav-customer-orders"]', placement: 'right', title: 'Bestellungen', body: 'Jeder Kauf mit Lizenzschlüssel, Download und Rechnung — alles in einer Zeile.' },
    { target: '[data-testid="dashboard-settings-btn"]', placement: 'bottom', title: 'Widgets ein- und ausblenden', body: 'Über das Zahnrad bestimmst du, was auf dem jeweiligen Tab angezeigt wird.' },
    { target: '[data-testid="aether-nav-logout"]', placement: 'right', title: 'Abmelden', body: 'Und hier meldest du dich wieder ab. Das war’s!' },
  ],
};

/* Ein Schritt ist nur brauchbar, wenn sein Ziel WIRKLICH sichtbar ist.
 * Vorher wurde bei fehlendem Ziel nur rect=null gesetzt — das ergab eine
 * vollflächige Abdunklung mit zentrierter Karte, sah also aus, als markiere
 * die Tour „nichts" bzw. „irgendwas dazwischen". Zusätzlich zum Nicht-Finden
 * fangen wir hier zusammengeklappte (0×0) und aus dem Bild geschobene Ziele
 * ab — z. B. die Sidebar, die auf Mobil per translate ausgeblendet wird und
 * trotzdem im DOM steht. */
function isUsableTarget(el) {
  if (!el) return false;
  const cs = window.getComputedStyle(el);
  if (cs.visibility === 'hidden' || cs.display === 'none') return false;
  const r = el.getBoundingClientRect();
  // Zusammengeklappt / ohne Ausdehnung -> es gäbe nichts zu markieren.
  if (r.width < 8 || r.height < 8) return false;
  // Waagerechte Sichtbarkeit NUR prüfen, wenn die Fensterbreite überhaupt
  // bekannt ist: in eingebetteten und headless Ansichten meldet der Browser
  // 0, und wir würden sonst jeden einzelnen Schritt verwerfen — die Tour
  // bestünde dann nur noch aus der Begrüßung.
  const vw = window.innerWidth || document.documentElement.clientWidth || 0;
  if (vw > 0 && (r.right <= 0 || r.left >= vw)) return false;
  // Bewusst KEINE Opacity-Prüfung: Elemente, die gerade erst eingeblendet
  // werden (framer-motion), stehen kurzzeitig auf 0 und würden sonst aus der
  // Tour fliegen, obwohl sie gleich sichtbar sind.
  return true;
}

export function startAetherTour() {
  try { window.dispatchEvent(new Event('aether-start-tour')); } catch {}
}

export default function OnboardingTour({ role }) {
  const allSteps = STEPS[role];
  const storageKey = `aether_tour_${TOUR_VERSION}_${role}`;
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  // The steps for THIS run — resolved at start time, so a step whose anchor
  // isn't on screen never gets shown at all (instead of framing nothing).
  const [steps, setSteps] = useState(null);

  const begin = useCallback(() => {
    if (!allSteps) return;
    const usable = allSteps.filter(
      (s) => !s.target || isUsableTarget(document.querySelector(s.target)),
    );
    setSteps(usable);
    setIdx(0);
    setActive(true);
  }, [allSteps]);

  // Auto-start once per role, shortly after mount so the layout has settled.
  useEffect(() => {
    if (!allSteps) return undefined;
    let done = false;
    try { done = !!localStorage.getItem(storageKey); } catch {}
    if (done) return undefined;
    const t = setTimeout(begin, 900);
    return () => clearTimeout(t);
  }, [allSteps, storageKey, begin]);

  // Manual restart from anywhere.
  useEffect(() => {
    window.addEventListener('aether-start-tour', begin);
    return () => window.removeEventListener('aether-start-tour', begin);
  }, [begin]);

  const step = active && steps ? steps[idx] : null;

  const measure = useCallback(() => {
    if (!step || !step.target) { setRect(null); return; }
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  // Only scroll when the target is actually off-screen, then measure.
  //
  // Das war der eigentliche „markiert dazwischen"-Bug: scrollIntoView lief mit
  // behavior:'smooth', direkt danach wurde gemessen — also die Position VOR dem
  // Scrollen. Der Ring saß damit sichtbar auf der alten Stelle, bis ein
  // Nachmessen per Timeout (320 ms) hinterherkam; lief die weiche Animation
  // noch, war auch die zweite Messung daneben. Jetzt wird hart gescrollt
  // ('auto') und ERST danach gemessen — die Position stimmt sofort.
  useLayoutEffect(() => {
    if (!step) return undefined;
    if (!step.target) { setRect(null); return undefined; }
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return undefined; }
    let r = el.getBoundingClientRect();
    const inView = r.top >= 8 && r.bottom <= window.innerHeight - 8;
    if (!inView) {
      el.scrollIntoView({ block: 'center', behavior: 'auto' });
      r = el.getBoundingClientRect();
    }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    return undefined;
  }, [step]);

  // Keep the spotlight glued to the target on scroll/resize.
  useEffect(() => {
    if (!active) return undefined;
    const onWin = () => measure();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [active, measure]);

  const finish = useCallback(() => {
    setActive(false);
    try { localStorage.setItem(storageKey, '1'); } catch {}
  }, [storageKey]);

  useEffect(() => {
    if (!active || !steps) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight') setIdx((i) => Math.min(i + 1, steps.length - 1));
      else if (e.key === 'ArrowLeft') setIdx((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, steps, finish]);

  if (!step) return null;

  const isLast = idx === steps.length - 1;
  const next = () => { if (isLast) finish(); else setIdx((i) => i + 1); };
  const prev = () => setIdx((i) => Math.max(i - 1, 0));

  // Card placement: near the target when we have a rect, else centered.
  const cardW = 320;
  const gap = 14;
  let cardStyle;
  // Fenstermaße defensiv: meldet der Browser 0 (eingebettete/headless Ansicht),
  // ergab `innerWidth - cardW - 16` eine NEGATIVE Obergrenze und die Karte
  // rutschte komplett aus dem Bild. Dasselbe passiert auf schmalen Handys,
  // sobald das Fenster schmaler als die Karte ist.
  const vw = window.innerWidth || document.documentElement.clientWidth || 1024;
  const vh = window.innerHeight || document.documentElement.clientHeight || 768;
  if (rect) {
    const place = step.placement || 'bottom';
    const clampLeft = (x) => Math.max(8, Math.min(x, Math.max(8, vw - cardW - 8)));
    const clampTop = (y) => Math.max(8, Math.min(y, Math.max(8, vh - 210)));
    if (place === 'right') {
      cardStyle = { top: clampTop(rect.top), left: clampLeft(rect.left + rect.width + gap) };
    } else if (place === 'top') {
      cardStyle = { top: clampTop(rect.top - 190), left: clampLeft(rect.left) };
    } else {
      cardStyle = { top: clampTop(rect.top + rect.height + gap), left: clampLeft(rect.left) };
    }
  } else {
    cardStyle = { top: Math.max(8, vh / 2 - 130), left: Math.max(8, vw / 2 - cardW / 2) };
  }

  // WICHTIG: per Portal direkt an <body>. Die Positionen von Ring und Karte
  // stammen aus getBoundingClientRect(), sind also Viewport-Koordinaten. Ein
  // `position: fixed` richtet sich aber NICHT am Viewport aus, sobald ein
  // Vorfahre transform/filter/backdrop-filter setzt — und das Dashboard ist
  // voller Glas-Flächen mit backdrop-filter. Dann verschiebt sich das ganze
  // Overlay um den Versatz dieses Vorfahren, und der Ring landet neben dem
  // Ziel („markiert dazwischen"). Am <body> kann das nicht passieren.
  return createPortal((
    <div className="fixed inset-0 z-[200]" role="dialog" aria-modal="true" aria-label="Dashboard-Tour">
      {/* Dim + spotlight hole (or full dim for centered steps).
          Die beiden Zweige tragen bewusst verschiedene key — sonst verwendet
          React denselben <div> wieder: der Ring erbte dann die Position der
          Vollfläche (0,0) und animierte per transition aus der Ecke ans Ziel,
          was aussah, als markiere er „dazwischen". Getrennte Knoten heißt: der
          Ring erscheint sofort exakt auf dem Ziel. Und die Position wird NICHT
          mehr animiert (nur eingeblendet), damit er beim Schrittwechsel hart
          aufs neue Element springt statt sichtbar dorthin zu gleiten. */}
      {rect ? (
        <div
          key="spotlight"
          className="absolute rounded-xl pointer-events-none"
          style={{
            top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
            boxShadow: '0 0 0 9999px rgba(3,3,3,0.74)', border: '1.5px solid rgba(249,115,22,0.6)',
          }}
        />
      ) : (
        <div key="dim" className="absolute inset-0" style={{ background: 'rgba(3,3,3,0.74)' }} />
      )}

      {/* Card */}
      <div className="absolute monolith-glass p-5 w-[320px] max-w-[calc(100vw-32px)]" style={{ ...cardStyle, transition: 'top 450ms cubic-bezier(0.4,0,0.2,1), left 450ms cubic-bezier(0.4,0,0.2,1)' }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-orange-300">
            <Sparkles size={14} />
            <span className="font-mono text-[9px] uppercase tracking-widest">Tour · {idx + 1}/{steps.length}</span>
          </div>
          <button onClick={finish} className="text-white/30 hover:text-white" title="Tour beenden"><X size={15} /></button>
        </div>
        <h3 className="text-white text-base font-medium mb-1.5">{step.title}</h3>
        <p className="text-white/60 text-[13px] leading-relaxed">{step.body}</p>
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-4 bg-orange-400' : 'w-1.5 bg-white/20'}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button onClick={prev} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/5" title="Zurück"><ArrowLeft size={14} /></button>
            )}
            <button
              onClick={next}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest text-black transition hover:scale-[1.03]"
              style={{ background: 'var(--primary, #F97316)' }}
            >
              {isLast ? 'Fertig' : 'Weiter'} {!isLast && <ArrowRight size={12} />}
            </button>
          </div>
        </div>
        {!isLast && (
          <button onClick={finish} className="mt-3 w-full text-center font-mono text-[9px] uppercase tracking-widest text-white/25 hover:text-white/50">
            Überspringen
          </button>
        )}
      </div>
    </div>
  ), document.body);
}
